import { useEffect, useRef, useState } from "react";

const ZONE_COLOR       = "#3dffa0";
const ARROW_COLOR      = "#ffe600";
const TRACK_BG         = "#0d0025";
const TRACK_BORDER     = "#9b5de533";
const SPEED_BASE       = 0.6;   // % per frame at 60fps
const SPEED_FACTOR     = 0.012; // extra speed per circle

// Clamp helper
const clamp = (v, lo, hi) => Math.max(lo, Math.min(hi, v));

// ── Animated target strip ──────────────────────────────────────────────────────
function TargetStrip({ zoneW, active, lastPos, lastHit, tv }) {
  const canvasRef = useRef(null);
  const stateRef  = useRef({ pos: 0, dir: 1, frame: 0 });
  const rafRef    = useRef(null);

  const H  = tv ? 120 : 80;
  const fs = (a, b) => tv ? a : b;

  function draw(canvas, pos) {
    if (!canvas) return;
    const W   = canvas.width;
    const ctx = canvas.getContext("2d");
    ctx.clearRect(0, 0, W, H);

    // Track background
    ctx.fillStyle = TRACK_BG;
    ctx.strokeStyle = TRACK_BORDER;
    ctx.lineWidth = 2;
    const pad = fs(24, 16);
    const r   = fs(12, 8);
    ctx.beginPath();
    ctx.roundRect(pad, H * 0.3, W - pad * 2, H * 0.4, r);
    ctx.fill();
    ctx.stroke();

    const trackLeft  = pad;
    const trackW     = W - pad * 2;
    const trackY     = H * 0.5;

    // Zone (always centred at 50%)
    const zoneLeft  = trackLeft + (trackW * (50 - zoneW / 2) / 100);
    const zoneRight = trackLeft + (trackW * (50 + zoneW / 2) / 100);
    const zoneH     = H * 0.4;
    ctx.fillStyle = ZONE_COLOR + "33";
    ctx.strokeStyle = ZONE_COLOR;
    ctx.lineWidth = fs(3, 2);
    ctx.beginPath();
    ctx.roundRect(zoneLeft, H * 0.3, zoneRight - zoneLeft, zoneH, r);
    ctx.fill();
    ctx.stroke();

    // Centre tick
    const cx = trackLeft + trackW * 0.5;
    ctx.strokeStyle = ZONE_COLOR + "88";
    ctx.lineWidth = fs(2, 1.5);
    ctx.setLineDash([4, 4]);
    ctx.beginPath();
    ctx.moveTo(cx, H * 0.25);
    ctx.lineTo(cx, H * 0.75);
    ctx.stroke();
    ctx.setLineDash([]);

    // Arrow
    const ax = trackLeft + (trackW * clamp(pos, 0, 100) / 100);
    const arrowH = fs(32, 22);
    const arrowW = fs(14, 10);
    ctx.fillStyle = active ? ARROW_COLOR : (lastHit ? ZONE_COLOR : "#f44336");
    ctx.shadowColor = active ? ARROW_COLOR : "transparent";
    ctx.shadowBlur  = active ? 18 : 0;
    ctx.beginPath();
    ctx.moveTo(ax, trackY + arrowH / 2);
    ctx.lineTo(ax - arrowW / 2, trackY - arrowH / 2);
    ctx.lineTo(ax + arrowW / 2, trackY - arrowH / 2);
    ctx.closePath();
    ctx.fill();
    ctx.shadowBlur = 0;

    // Edge labels
    ctx.fillStyle = "rgba(255,255,255,0.25)";
    ctx.font = `${fs(13, 10)}px 'Fredoka One', sans-serif`;
    ctx.textAlign = "left";
    ctx.fillText("0%", trackLeft + 4, H * 0.26);
    ctx.textAlign = "right";
    ctx.fillText("100%", trackLeft + trackW - 4, H * 0.26);
  }

  // Resize canvas to parent width
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ro = new ResizeObserver(() => {
      canvas.width = canvas.offsetWidth;
      draw(canvas, stateRef.current.pos);
    });
    ro.observe(canvas.parentElement);
    canvas.width = canvas.offsetWidth || 600;
    draw(canvas, stateRef.current.pos);
    return () => ro.disconnect();
  }, [zoneW, lastHit]); // eslint-disable-line

  // Animation loop
  useEffect(() => {
    if (!active) {
      // Show final frozen position
      if (lastPos !== null) {
        stateRef.current.pos = lastPos;
        draw(canvasRef.current, lastPos);
      }
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      return;
    }

    const st = stateRef.current;
    // Keep current pos and dir, just (re)start animation
    const speed = SPEED_BASE; // circle speed is controlled by backend

    function loop() {
      st.pos += st.dir * speed;
      if (st.pos >= 100) { st.pos = 100; st.dir = -1; }
      if (st.pos <= 0)   { st.pos = 0;   st.dir =  1; }
      draw(canvasRef.current, st.pos);
      rafRef.current = requestAnimationFrame(loop);
    }

    rafRef.current = requestAnimationFrame(loop);
    return () => { if (rafRef.current) cancelAnimationFrame(rafRef.current); };
  }, [active, zoneW]); // eslint-disable-line

  return (
    <canvas
      ref={canvasRef}
      height={H}
      style={{ width: "100%", display: "block" }}
    />
  );
}

// ── Main Host component ────────────────────────────────────────────────────────
export default function SniperHost({ turnData, shotResult, players, tv }) {
  const fs = (a, b) => tv ? a : b;

  // Which phase are we in
  const phase = shotResult ? "result" : (turnData ? "aiming" : "waiting");

  const zoneW       = turnData?.zone_w ?? shotResult?.zone_w ?? 40;
  const currentPlayer = turnData?.current_player ?? shotResult?.player ?? "";
  const alive       = turnData?.alive ?? shotResult?.alive ?? [];
  const eliminated  = turnData?.eliminated ?? shotResult?.eliminated ?? [];
  const scores      = turnData?.scores ?? shotResult?.scores ?? {};
  const circle      = turnData?.circle ?? 0;

  // For frozen display after shot
  const lastPos = shotResult ? shotResult.pos : null;
  const lastHit = shotResult ? shotResult.hit : null;

  const active = phase === "aiming";

  return (
    <div style={{
      width: "100%", maxWidth: "720px", margin: "0 auto",
      padding: fs("1.5rem 1rem", "0.75rem 0.5rem"),
      display: "flex", flexDirection: "column", gap: fs("1.2rem", "0.75rem"),
    }}>

      {/* Header: зона + круг */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: "0.5rem" }}>
        <div style={{
          background: "#3dffa011", border: "1.5px solid #3dffa055",
          borderRadius: "0.6rem", padding: "0.3rem 0.8rem",
          fontFamily: "'Fredoka One', sans-serif", fontSize: fs("1rem", "0.8rem"), color: "#3dffa0",
        }}>
          🎯 Зона: <strong>{zoneW.toFixed(1)}%</strong>
        </div>
        <div style={{
          background: "#ffe60011", border: "1.5px solid #ffe60055",
          borderRadius: "0.6rem", padding: "0.3rem 0.8rem",
          fontFamily: "'Fredoka One', sans-serif", fontSize: fs("1rem", "0.8rem"), color: "#ffe600",
        }}>
          🔄 Круг {circle + 1}
        </div>
      </div>

      {/* Current player */}
      <div style={{ textAlign: "center" }}>
        {phase === "aiming" && (
          <p style={{ fontFamily: "'Fredoka One', sans-serif", fontSize: fs("1.5rem", "1.1rem"), color: "#FF6B6B" }}>
            🔫 Стреляет: <strong style={{ color: "#fff" }}>{currentPlayer}</strong>
          </p>
        )}
        {phase === "result" && (
          <p style={{
            fontFamily: "'Fredoka One', sans-serif",
            fontSize: fs("1.5rem", "1.1rem"),
            color: lastHit ? "#3dffa0" : "#f44336",
          }}>
            {lastHit
              ? `✅ ${currentPlayer} попал! +${shotResult.score} очков`
              : `💀 ${currentPlayer} промахнулся — вылетает!`}
          </p>
        )}
      </div>

      {/* Target strip */}
      <div style={{
        background: "#110025", border: "2px solid #9b5de533",
        borderRadius: "1rem", padding: fs("1rem", "0.6rem"),
        boxShadow: active ? "0 0 30px #9b5de522" : "none",
        transition: "box-shadow 0.3s",
      }}>
        <TargetStrip
          zoneW={zoneW}
          active={active}
          lastPos={lastPos}
          lastHit={lastHit}
          tv={tv}
        />
        {/* Zone width hint */}
        <p style={{
          textAlign: "center", marginTop: "0.5rem",
          fontFamily: "'Nunito', sans-serif", fontSize: fs("0.8rem", "0.65rem"),
          color: "#555",
        }}>
          Зелёная зона — область попадания · Центр = максимум очков
        </p>
      </div>

      {/* Shot result detail */}
      {phase === "result" && lastHit && (
        <div style={{
          textAlign: "center",
          fontFamily: "'Nunito', sans-serif", fontSize: fs("0.9rem", "0.75rem"),
          color: "#aaa",
        }}>
          Точность: <strong style={{ color: "#ffe600" }}>{shotResult.score}%</strong> от идеала
          {shotResult.timed_out && " (время вышло)"}
        </div>
      )}

      {/* Players bar */}
      <div style={{ display: "flex", flexWrap: "wrap", gap: "0.4rem", justifyContent: "center" }}>
        {players.map(p => {
          const isElim   = eliminated.includes(p.name);
          const isCurrent = p.name === currentPlayer;
          return (
            <div key={p.name} style={{
              background: isElim ? "#200000" : isCurrent ? "#FF6B6B22" : "#110825",
              border: `2px solid ${isElim ? "#f44336" : isCurrent ? "#FF6B6B" : "#ffffff11"}`,
              borderRadius: "0.6rem", padding: "0.25rem 0.7rem",
              fontFamily: "'Fredoka One', sans-serif", fontSize: fs("0.9rem", "0.75rem"),
              color: isElim ? "#f44336" : isCurrent ? "#FF6B6B" : "#aaa",
            }}>
              {isElim ? "💀" : isCurrent ? "🔫" : ""} {p.name}
              {scores[p.name] > 0 && (
                <span style={{ color: "#ffe600", marginLeft: "5px" }}>{scores[p.name]}</span>
              )}
            </div>
          );
        })}
      </div>

      {/* Alive count */}
      <div style={{ textAlign: "center", fontFamily: "'Nunito', sans-serif", fontSize: fs("0.85rem", "0.7rem"), color: "#555" }}>
        Выживших: <strong style={{ color: "#3dffa0" }}>{alive.length}</strong>
        {" · "}
        Вылетело: <strong style={{ color: "#f44336" }}>{eliminated.length}</strong>
      </div>
    </div>
  );
}
